/**
 * GOAT-OVERRIDE(pdp-section-render): narrow the PDP variant-change request to one section.
 *
 * `assets/variant-picker.js` is byte-identical to pristine Horizon 4.1.5, so this patches its
 * prototype from a GOAT-owned module instead of editing it — nothing to re-apply after the next
 * vendor merge beyond confirming upstream's own `SECTION_ID_MAP` still has no PDP entry.
 *
 * Upstream maps only three hosts (`quick-add-component`, `swatches-variant-picker-component`,
 * `featured-product-information`) to a `section_id`. On a real product page none of them match, so
 * `buildRequestUrl` falls through to `${productUrl}?option_values=…` — a request for the ENTIRE
 * product page. Shopify then re-renders every section in templates/product.json (the product grid,
 * the curated review row, the lifestyle collection, product recommendations with their related-
 * products lookup, the FAQ, header and footer) and ships them all, while the client keeps exactly
 * one: the main product section. Every product card in those rows re-runs the swatch resolver, so
 * the shopper waits on work nothing displays.
 *
 * Every consumer of the response reads a node that lives inside the main product section —
 * media-gallery, product-price, product-sku, product-inventory, product-form-component,
 * quantity-selector-component, price-per-item, sticky-add-to-cart and loyalty-points — and the
 * variant JSON the picker itself parses is rendered by snippets/variant-main-picker.liquid, also
 * inside it. The section id is read from the DOM rather than hardcoded, so alternate product
 * templates keep working.
 *
 * Deliberately NOT applied when:
 *   - upstream already chose a section (the three mapped hosts);
 *   - the click loads a different product (combined listings). `updateMain()` does
 *     `newHtml.querySelector('main')` and throws without it, and a `?section_id=` response has no
 *     <main>;
 *   - the picker has no `.shopify-section` ancestor, which is the case inside the layout-level
 *     Quick Shop / quick-add dialogs.
 */
import VariantPicker from '@theme/variant-picker';

const SECTION_ID_PREFIX = 'shopify-section-';
const originalBuildRequestUrl = VariantPicker.prototype.buildRequestUrl;

VariantPicker.prototype.buildRequestUrl = function goatBuildRequestUrl(selectedOption, ...rest) {
  const url = originalBuildRequestUrl.call(this, selectedOption, ...rest);

  try {
    if (typeof url !== 'string' || url.includes('section_id=')) return url;

    // Combined listing: the response must be a full document so `updateMain()` finds <main>.
    if (selectedOption?.dataset?.connectedProductUrl) return url;

    const sectionElement = this.closest('.shopify-section');
    const sectionId = sectionElement?.id;
    if (!sectionId || !sectionId.startsWith(SECTION_ID_PREFIX)) return url;

    const section = sectionId.slice(SECTION_ID_PREFIX.length);
    if (!section) return url;

    return `${url}${url.includes('?') ? '&' : '?'}section_id=${encodeURIComponent(section)}`;
  } catch {
    // Never let the narrowing break a variant change; fall back to upstream's URL.
    return url;
  }
};

const originalUpdateSelectedOption = VariantPicker.prototype.updateSelectedOption;

// Restrike colour swatches for the new size in the same frame; the section re-render lands later with the same state.
VariantPicker.prototype.updateSelectedOption = function goatUpdateSelectedOption(target) {
  originalUpdateSelectedOption.call(this, target);

  try {
    syncSelectionAvailability(this);
  } catch {
    // The re-rendered section still brings the server's strike state.
  }
};

/**
 * @param {HTMLElement} picker
 */
function syncSelectionAvailability(picker) {
  const form = picker.querySelector('[data-available-variants]');
  if (!(form instanceof HTMLElement)) return;

  /** @type {string[][]} */
  const availableVariants = JSON.parse(form.dataset.availableVariants || '[]');
  const groups = Array.from(picker.querySelectorAll('.variant-option'));
  if (!availableVariants.length || groups.length !== availableVariants[0]?.length) return;

  const selected = groups.map((group) => {
    const control = group.querySelector('input:checked, select');
    return control instanceof HTMLInputElement || control instanceof HTMLSelectElement ? control.value : undefined;
  });
  if (selected.includes(undefined)) return;

  const available = new Set(availableVariants.map((options) => JSON.stringify(options)));

  groups.forEach((group, groupIndex) => {
    /** @type {NodeListOf<HTMLInputElement>} */
    const swatchInputs = group.querySelectorAll(
      '.variant-option__button-label--has-swatch input[data-option-available="true"]'
    );

    for (const input of swatchInputs) {
      const options = [...selected];
      options[groupIndex] = input.value;

      if (available.has(JSON.stringify(options))) {
        delete input.dataset.selectionAvailable;
      } else {
        input.dataset.selectionAvailable = 'false';
      }
    }
  });
}
